import type { User } from "@/lib/types";
import { isProfileCompleteForAccess } from "@/lib/profileCompletion";
import { profileLocationUsableForNearMe } from "@/lib/notifications/homepage-near-me-quick-opt-in";
import {
  resolveNotificationSetupSnoozeProfile,
  shouldShowNotificationSetupBanner,
  type NotificationSetupBannerPolicyResult,
} from "@/lib/notifications/notification-setup-banner-policy";

export const PROFILE_NOTIFICATION_SETUP_HREF =
  "/profile?tab=notifications&nearMeSource=setup_banner";

export const PROFILE_LOCATION_SETUP_HREF = "/profile?tab=profile";

export const NOTIFICATION_SETUP_BANNER_SUPPRESSED_PREFIXES = [
  "/login",
  "/signup",
  "/callback",
  "/verify-account",
  "/complete-profile",
  "/onboarding",
  "/admin",
  "/ds",
  "/design-system",
] as const;

export type NearMeGapReason = "opt_out" | "no_location" | null;

export type NotificationSetupGaps = {
  nearMeGap: boolean;
  nearMeGapReason: NearMeGapReason;
  pushGap: boolean;
  pushPermissionDenied: boolean;
};

export type NotificationSetupBannerMode = "both" | "near_me" | "push" | null;

export type NotificationPrefsSnapshot = {
  notify_new_listings_near_me: boolean | null;
};

export type NotificationSetupEligibilityInput = {
  user: User | null;
  authLoading: boolean;
  registrationOnboardingComplete: boolean;
  nearMePlatformEnabled: boolean;
  growthDefaultsOn: boolean;
  prefs: NotificationPrefsSnapshot | null;
  prefsLoaded: boolean;
  pushSupported: boolean;
  pushVapidConfigured: boolean;
  pushPermission: NotificationPermission | "unsupported";
  browserPushSubscribed: boolean;
  pathname: string;
  profileTab: string | null;
  dismissedAtIso: string | null;
  dismissCount: number;
  now?: Date;
};

export type NotificationSetupEligibilityResult = {
  gaps: NotificationSetupGaps;
  mode: NotificationSetupBannerMode;
  policy: NotificationSetupBannerPolicyResult;
  visible: boolean;
  passiveChipVisible: boolean;
  profileHref: string;
};

export function isNotificationSetupBannerRouteSuppressed(
  pathname: string,
  profileTab: string | null,
): boolean {
  if (
    NOTIFICATION_SETUP_BANNER_SUPPRESSED_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    )
  ) {
    return true;
  }
  if (pathname === "/profile" && profileTab === "notifications") {
    return true;
  }
  return false;
}

export function isNearMePreferenceOn(
  prefs: NotificationPrefsSnapshot | null,
  growthDefaultsOn: boolean,
): boolean {
  if (!prefs || prefs.notify_new_listings_near_me === null) {
    return growthDefaultsOn;
  }
  return Boolean(prefs.notify_new_listings_near_me);
}

export function computeNotificationSetupGaps(input: {
  user: User | null;
  nearMePlatformEnabled: boolean;
  growthDefaultsOn: boolean;
  prefs: NotificationPrefsSnapshot | null;
  pushSupported: boolean;
  pushVapidConfigured: boolean;
  pushPermission: NotificationPermission | "unsupported";
  browserPushSubscribed: boolean;
}): NotificationSetupGaps {
  const pushPermissionDenied = input.pushPermission === "denied";

  let nearMeGap = false;
  let nearMeGapReason: NearMeGapReason = null;

  if (input.nearMePlatformEnabled && input.user) {
    const nearMeOn = isNearMePreferenceOn(input.prefs, input.growthDefaultsOn);
    const locationOk = profileLocationUsableForNearMe({
      location: input.user.location,
      location_state_id: input.user.location_state_id,
      location_lga_id: input.user.location_lga_id,
      location_neighborhood_id: input.user.location_neighborhood_id,
    });

    if (!nearMeOn) {
      nearMeGap = true;
      nearMeGapReason = "opt_out";
    } else if (!locationOk) {
      nearMeGap = true;
      nearMeGapReason = "no_location";
    }
  }

  const pushGap =
    input.pushSupported &&
    input.pushVapidConfigured &&
    !pushPermissionDenied &&
    !(input.pushPermission === "granted" && input.browserPushSubscribed);

  return {
    nearMeGap,
    nearMeGapReason,
    pushGap,
    pushPermissionDenied,
  };
}

export function resolveNotificationSetupBannerMode(
  gaps: NotificationSetupGaps,
): NotificationSetupBannerMode {
  if (!gaps.nearMeGap && !gaps.pushGap) {
    return null;
  }
  if (gaps.nearMeGap && gaps.pushGap) {
    return "both";
  }
  if (gaps.nearMeGap) {
    return "near_me";
  }
  return "push";
}

export function resolveNotificationSetupProfileHref(
  nearMeGapReason: NearMeGapReason,
): string {
  if (nearMeGapReason === "no_location") {
    return PROFILE_LOCATION_SETUP_HREF;
  }
  return PROFILE_NOTIFICATION_SETUP_HREF;
}

export function evaluateNotificationSetupEligibility(
  input: NotificationSetupEligibilityInput,
): NotificationSetupEligibilityResult {
  const gaps = computeNotificationSetupGaps(input);
  const mode = resolveNotificationSetupBannerMode(gaps);
  const hasSetupGap = Boolean(mode);
  const snoozeProfile = resolveNotificationSetupSnoozeProfile(gaps);
  const policy = shouldShowNotificationSetupBanner({
    hasSetupGap,
    dismissedAtIso: input.dismissedAtIso,
    dismissCount: input.dismissCount,
    snoozeProfile,
    now: input.now,
  });

  const userReady =
    !input.authLoading &&
    Boolean(input.user) &&
    input.registrationOnboardingComplete &&
    isProfileCompleteForAccess(input.user!);

  const routeAllowed = !isNotificationSetupBannerRouteSuppressed(
    input.pathname,
    input.profileTab,
  );

  const visible =
    userReady &&
    input.prefsLoaded &&
    routeAllowed &&
    hasSetupGap &&
    policy.show;

  const passiveChipVisible =
    userReady &&
    input.prefsLoaded &&
    routeAllowed &&
    hasSetupGap &&
    policy.passiveOnly;

  const profileHref = resolveNotificationSetupProfileHref(
    gaps.nearMeGap ? gaps.nearMeGapReason : null,
  );

  return {
    gaps,
    mode,
    policy,
    visible,
    passiveChipVisible,
    profileHref,
  };
}

export type NotificationSetupBannerCopy = {
  title: string;
  body: string;
  primaryCta: string;
  secondaryCta: string;
};

export function getNotificationSetupBannerCopy(
  mode: NotificationSetupBannerMode,
  nearMeGapReason: NearMeGapReason,
): NotificationSetupBannerCopy | null {
  if (!mode) return null;

  if (mode === "both") {
    return {
      title: "Finish your alert setup",
      body: "Get instant alerts for orders, listing updates, and new items near you on this device.",
      primaryCta: "Finish setup",
      secondaryCta: "Not now",
    };
  }

  if (mode === "near_me") {
    if (nearMeGapReason === "no_location") {
      return {
        title: "Add your location for local alerts",
        body: "Near-me alerts need your profile location to match listings.",
        primaryCta: "Add location",
        secondaryCta: "Not now",
      };
    }
    return {
      title: "Never miss a deal near you",
      body: "Turn on alerts when new listings are approved in your area.",
      primaryCta: "Turn on near-me alerts",
      secondaryCta: "Not now",
    };
  }

  return {
    title: "Enable notifications on this device",
    body: "Get instant alerts for orders, listing updates, and new items near you — optional if you already use email.",
    primaryCta: "Enable notifications",
    secondaryCta: "Not now",
  };
}
